// Auto-generated from shared/video_generation_schema.json
// Contains schema field definitions for the SchemaAdapter

export const SCHEMA_FIELDS = {
  topic: {
    type: 'string',
    required: false,
    default: '',
    description: 'Text prompt for video generation (required only when narration_mode is llm_generated)',
  },
  duration_minutes: {
    type: 'number',
    required: false,
    default: 0.25,
    min: 0.17,
    max: 180,
    description: 'Video duration in minutes',
  },
  resolution: {
    type: 'string',
    required: false,
    default: '1920x1080',
    enum: ['1920x1080', '1280x720', '1080x1920', '720x1280', '1080x1080', '720x720', '512x512', '912x512', '512x912', '3840x2160'],
    description: 'Video resolution',
  },
  fps: {
    type: 'integer',
    required: false,
    default: 30,
    min: 24,
    max: 60,
    description: 'Frames per second',
  },
  style: {
    type: 'string',
    required: false,
    default: 'cinematic',
    description: 'Video style preset',
  },
  subtitles: {
    type: 'boolean',
    required: false,
    default: false,
    description: 'Enable subtitle generation',
  },
  subtitle_mode: {
    type: 'string',
    required: false,
    default: 'soft',
    enum: ['soft', 'hard'],
    description: 'Subtitle mode',
  },
  adjust_timing_to_narration: {
    type: 'boolean',
    required: false,
    default: true,
    description: 'Adjust scene timing to match narration',
  },
  language: {
    type: 'string',
    required: true,
    description: 'Language for narration (REQUIRED - no fallbacks)',
  },
  voice_id: {
    type: 'string',
    required: true,
    default: 'af_bella',
    description: 'Voice ID for TTS. Default af_bella is a Kokoro voice and matches the default tts_provider. When tts_provider changes, the UI or caller is responsible for picking a compatible voice_id from that provider catalog.',
  },
  tts_provider: {
    type: 'string',
    required: true,
    default: 'kokoro',
    enum: ['kokoro', 'edge_tts', 'google', 'pyttsx3', 'f5_tts', 'chatterbox', 'minimax', 'chatterbox_turbo', 'elevenlabs'],
    description: 'TTS provider',
  },
  llm_provider: {
    type: 'string',
    required: false,
    default: 'openrouter',
    description: 'LLM provider for content generation',
  },
  llm_model: {
    type: 'string',
    required: false,
    default: 'google/gemini-2.5-flash-lite',
    description: 'LLM model for content generation',
  },
  subtitle_languages: {
    type: 'array',
    items: {
      type: 'string',
    },
    required: false,
    default: [],
    description: 'List of language codes for multilingual subtitle generation',
  },
  translation_provider: {
    type: 'string',
    required: false,
    default: 'nllb',
    enum: ['nllb', 'google'],
    description: 'Translation provider for subtitle generation (nllb=free, google=paid)',
  },
  narration_mode: {
    type: 'string',
    required: false,
    default: 'llm_generated',
    enum: ['llm_generated', 'user_provided', 'audio_upload', 'video_upload', 'none'],
    description: 'Narration generation mode (llm_generated, user_provided, audio_upload, video_upload, or none for video-only without audio)',
  },
  audio_source: {
    type: 'string',
    required: false,
    enum: ['tts', 'music'],
    default: 'tts',
    description: 'Source of the final audio track. tts = TTS-from-narration (default). music = use pre-generated music audio + lyrics.',
  },
  music_audio_path: {
    type: 'string',
    required: false,
    description: 'Absolute URL (gs:// / https://) or local filesystem path to a music MP3. Required when audio_source is music. The pipeline downloads remote URLs to the worker before processing.',
  },
  music_lyrics_text: {
    type: 'string',
    required: false,
    description: 'Raw lyrics text (may include Lyria [HH:MM:SS] or [s.s:] timestamps). When omitted, falls back to music_prompt for visual prompt generation.',
  },
  music_prompt: {
    type: 'string',
    required: false,
    description: 'Original music generation prompt; used as ghost-narration for instrumental tracks (no lyrics).',
  },
  music_beat_sync_enabled: {
    type: 'boolean',
    required: false,
    default: true,
    description: 'When true (default), visual cuts are snapped to musical downbeats via librosa beat detection.',
  },
  user_narration_text: {
    type: 'string',
    required: false,
    default: '',
    description: 'User-provided narration text (only used when narration_mode is user_provided)',
  },
  audio_languages: {
    type: 'array',
    items: {
      type: 'object',
      properties: {'language': {'type': 'string', 'description': 'Language code (e.g. en, es, pt)'}, 'provider': {'type': 'string', 'description': 'TTS provider for this language (optional)'}, 'voice_id': {'type': 'string', 'description': 'Voice ID for this language (optional)'}},
      required: ['language'],
    },
    required: false,
    default: [],
    description: 'List of languages with optional per-language TTS provider and voice configuration for multilingual audio generation',
  },
  media_type: {
    type: 'string',
    required: false,
    default: 'images',
    enum: ['images', 'videos'],
    description: 'User-selected media mode: images for stock/AI images, videos for stock/AI video clips',
  },
  image_source: {
    type: 'string',
    required: false,
    default: 'stock',
    enum: ['stock', 'ai', 'local_assets'],
    description: 'Source for visual media: stock for Pexels/Pixabay stock videos, ai for AI-generated images via Fooocus, local_assets for app screenshots (self-marketing)',
  },
  image_provider: {
    type: 'string',
    required: false,
    default: 'fooocus',
    description: 'AI image generation provider (used when image_source is ai)',
  },
  stock_image_provider: {
    type: 'string',
    required: false,
    default: 'pexels',
    enum: ['pexels', 'pixabay'],
    description: 'Stock image provider (used when image_source is stock)',
  },
  stock_video_provider: {
    type: 'string',
    required: false,
    default: 'pexels',
    enum: ['pexels', 'pixabay'],
    description: 'Stock video provider (used when video_source is stock)',
  },
  image_provider_for_video: {
    type: 'string',
    required: false,
    description: 'Image generator used to produce first frame when video_source is an I2V provider. Required when video_source has requires_image=true.',
    nullable: true,
  },
  i2v_clip_duration_override_s: {
    type: 'number',
    required: false,
    description: 'Optional per-clip duration override in seconds for I2V providers that allow user adjustment (currently only ai_framepack). Validated against AI_VIDEO_SOURCE_METADATA[provider].user_adjustable + min/max range. Null means use the provider recommended_clip_duration_s.',
    nullable: true,
  },
  fooocus_style_mode: {
    type: 'string',
    required: false,
    default: 'auto',
    enum: ['auto', 'manual'],
    description: 'Fooocus style selection mode (auto = LLM chooses, manual = user selects)',
  },
  fooocus_styles: {
    type: 'array',
    items: {
      type: 'string',
    },
    required: false,
    default: [],
    description: 'Selected Fooocus styles when in manual mode',
  },
  fooocus_style_description: {
    type: 'string',
    required: false,
    default: '',
    description: 'Natural language description for auto style mode',
  },
  fooocus_model: {
    type: 'string',
    required: false,
    description: 'Fooocus checkpoint model name override. When null, auto-selects based on output resolution orientation.',
    nullable: true,
  },
  comfyui_sd_model: {
    type: 'string',
    required: false,
    description: 'ComfyUI SD 1.5 checkpoint filename (used when image_provider is comfyui-sd). Null = recommended default.',
    nullable: true,
  },
  comfyui_sd_steps: {
    type: 'integer',
    required: false,
    description: 'ComfyUI SD 1.5 KSampler steps. Null = per-family default (Hyper vs standard).',
    nullable: true,
  },
  comfyui_sd_loras: {
    type: 'array',
    items: {
      type: 'object',
    },
    required: false,
    default: [],
    description: 'ComfyUI SD 1.5 LoRA stack: list of {filename, strength} entries applied on top of the checkpoint',
  },
  image_negative_prompt: {
    type: 'string',
    required: false,
    default: '',
    description: 'Global negative prompt for AI-generated images (Fooocus / ComfyUI-SD). Merged with the per-scene negative prompt the LLM generates, never replaces it. Empty = use only the LLM/per-scene negatives.',
  },
  image_use_suggested_quality: {
    type: 'boolean',
    required: false,
    default: false,
    description: 'When true, append SUGGESTED_POSITIVE_PROMPT quality boosters to each generated image prompt and merge SUGGESTED_NEGATIVE_PROMPT into the negative (Fooocus / ComfyUI-SD only).',
  },
  phonetic_correction: {
    type: 'boolean',
    required: false,
    default: false,
    description: 'Enable phonetic correction for TTS pronunciation (advanced feature - consumes extra resources)',
  },
  generate_seo: {
    type: 'boolean',
    required: false,
    default: false,
    description: 'Generate YouTube SEO metadata (title, description, tags) after video generation',
  },
  subtitle_style: {
    type: 'object',
    required: false,
    description: 'Custom subtitle style configuration for hard-coded subtitles',
    properties: {
      font_family: {'type': 'string', 'default': 'Arial'},
      font_size: {'type': 'integer', 'default': 48},
      bold: {'type': 'boolean', 'default': false},
      italic: {'type': 'boolean', 'default': false},
      underline: {'type': 'boolean', 'default': false},
      strikeout: {'type': 'boolean', 'default': false},
      primary_color: {'type': 'string', 'default': '#FFFF00'},
      secondary_color: {'type': 'string', 'default': '#FF0000'},
      outline_color: {'type': 'string', 'default': '#000000'},
      shadow_color: {'type': 'string', 'default': '#000000'},
      outline_enabled: {'type': 'boolean', 'default': true},
      outline_size: {'type': 'integer', 'default': 2},
      shadow_enabled: {'type': 'boolean', 'default': true},
      shadow_size: {'type': 'integer', 'default': 1},
      text_opacity: {'type': 'number', 'default': 100},
      outline_opacity: {'type': 'number', 'default': 100},
      shadow_opacity: {'type': 'number', 'default': 50},
      alignment: {'type': 'string', 'default': 'bottom_center'},
      margin_left: {'type': 'integer', 'default': 10},
      margin_right: {'type': 'integer', 'default': 10},
      margin_vertical: {'type': 'integer', 'default': 10},
      blur_strength: {'type': 'number', 'default': 0},
      scale_x: {'type': 'number', 'default': 100},
      scale_y: {'type': 'number', 'default': 100},
      rotation: {'type': 'number', 'default': 0},
      spacing: {'type': 'number', 'default': 0},
      background_box_enabled: {'type': 'boolean', 'default': false},
      background_box_color: {'type': 'string', 'default': '#000000'},
      background_box_padding: {'type': 'integer', 'default': 5},
    },
  },
  emoticon_subtitles: {
    type: 'object',
    required: false,
    description: 'Optional emoticon overlay configuration for animated subtitle emoticons',
    properties: {
      enabled: {'type': 'boolean', 'default': false, 'description': 'Enable animated emoticon overlays on subtitles'},
      animation_type: {'type': 'string', 'enum': ['none', 'pop_in', 'bounce', 'pulse', 'shake', 'sparkle', 'float', 'slide_in', 'zoom_rotate'], 'default': 'pop_in', 'description': 'Animation style for the emoticon entrance'},
      position: {'type': 'string', 'enum': ['start', 'end', 'floating'], 'default': 'start', 'description': 'Placement of the emoticon relative to subtitle text'},
      min_confidence: {'type': 'number', 'default': 0.5, 'minimum': 0, 'maximum': 1, 'description': 'Minimum keyword-match confidence required to show an emoticon (0-1)'},
    },
  },
  effects_config: {
    type: 'object',
    required: false,
    default: {
      auto_effects: {'slow_zoom_on_static_images': true, 'fade_first_last': false, 'default_transition': 'none'},
      default_color_preset: 'none',
    },
    description: 'Global effects configuration for video generation',
    properties: {
      auto_effects: {'type': 'object', 'description': 'Automatic effect application settings', 'properties': {'slow_zoom_on_static_images': {'type': 'boolean', 'default': true, 'description': 'Apply slow 8% zoom-in on static images (AI or stock) to add dynamic movement'}, 'fade_first_last': {'type': 'boolean', 'default': false, 'description': 'When false (default), the LLM effects picker decides per-video whether to apply fade-in/fade-out. When true, fades are forced on regardless of picker output.'}, 'default_transition': {'type': 'string', 'enum': ['none', 'crossfade', 'fadeblack', 'fadewhite', 'dissolve'], 'default': 'none', 'description': 'Default per-image transition fallback. "none" (default) lets the LLM picker decide per-image; set to "crossfade" or "fade_through_black" to force a transition style on every consecutive image pair.'}}},
      default_color_preset: {'type': 'string', 'enum': ['none', 'cinematic', 'warm', 'cool', 'muted', 'vintage'], 'default': 'none', 'description': 'Default color grading preset applied to all resources'},
    },
  },
  video_source: {
    type: 'string',
    required: false,
    default: 'stock',
    enum: ['stock', 'youtube', 'ai_veo', 'ai_kling', 'ai_vidu', 'ai_wan21', 'ai_ltx2', 'ai_animatediff', 'ai_deforum', 'ai_wan26_t2v', 'ai_wan26_i2v', 'ai_wan25_i2v', 'ai_kling_i2v_pro', 'ai_seedance_i2v', 'ai_sora2_i2v', 'ai_sora2_pro_i2v', 'ai_framepack', 'ai_infinitetalk', 'ai_kling_motion', 'ai_kling_o1_r2v', 'ai_seedance2', 'ai_director', 'ai_sora', 'upload_broll', 'ai_wan21_t2v_1_3b', 'ai_wan22_ti2v_5b', 'ai_wan22_ti2v_5b_i2v', 'ai_wan22_t2v_14b'],
    description: 'Source for video clips: stock footage, YouTube, or AI-generated video providers',
  },
  youtube_user_urls: {
    type: 'array',
    items: {
      type: 'string',
    },
    required: false,
    default: [],
    nullable: false,
    description: 'User-supplied YouTube URLs to use as the curation source. When non-empty, the pipeline skips the YouTube Data API search and analyzes only these videos. Accepts youtube.com/watch?v=, youtu.be/, and youtube.com/shorts/ formats. Capped at MAX_VIDEOS_TO_DOWNLOAD_PER_GENERATION (15) unique videos per generation.',
  },
  veo_config: {
    type: 'object',
    required: false,
    description: 'Configuration for Veo 3.1 AI video generation (only used when video_source is ai_veo)',
    properties: {
      quality: {'type': 'string', 'enum': ['fast', 'standard'], 'default': 'fast', 'description': 'Quality tier: fast (225 tokens/s) or standard (600 tokens/s)'},
      resolution: {'type': 'string', 'enum': ['720p', '1080p', '4k'], 'default': '1080p', 'description': 'Output resolution'},
      aspect_ratio: {'type': 'string', 'enum': ['16:9', '9:16', '1:1'], 'default': '16:9', 'description': 'Video aspect ratio'},
      enable_audio: {'type': 'boolean', 'default': true, 'description': 'Enable native audio generation'},
    },
  },
  kling_config: {
    type: 'object',
    required: false,
    description: 'Configuration for Kling AI video generation via fal.ai (only used when video_source is ai_kling)',
    properties: {
      model: {'type': 'string', 'enum': ['kling-v2.1-pro', 'kling-v2.5-turbo', 'kling-o1'], 'default': 'kling-v2.5-turbo', 'description': 'Kling model: turbo (fast/cheap), standard (balanced), o1 (transitions)'},
      duration: {'type': 'string', 'enum': ['5', '10'], 'default': '5', 'description': 'Video duration in seconds'},
      aspect_ratio: {'type': 'string', 'enum': ['16:9', '9:16', '1:1'], 'default': '16:9', 'description': 'Video aspect ratio'},
      mode: {'type': 'string', 'enum': ['std', 'pro'], 'default': 'std', 'description': 'Quality mode'},
    },
  },
  vidu_config: {
    type: 'object',
    required: false,
    description: 'Configuration for Vidu AI video generation (only used when video_source is ai_vidu)',
    properties: {
      model: {'type': 'string', 'enum': ['vidu-q3', 'vidu-q3-fast', 'vidu-q2'], 'default': 'vidu-q2', 'description': 'Vidu model: Q2 (budget, available). Q3 and Q3 Fast are enum-listed for forward compatibility but disabled in the UI until the official api.vidu.com Q3 slug is verified (see docs/modules/VIDU_Q3_ROUTING.md).'},
      duration: {'type': 'string', 'enum': ['4', '8', '16'], 'default': '8', 'description': 'Video duration in seconds'},
      aspect_ratio: {'type': 'string', 'enum': ['16:9', '9:16', '1:1', '4:3', '3:4'], 'default': '16:9', 'description': 'Video aspect ratio'},
      style: {'type': 'string', 'enum': ['general', 'anime'], 'default': 'general', 'description': 'Generation style'},
      enable_audio: {'type': 'boolean', 'default': true, 'description': 'Enable native audio generation (Q3/Q3 Fast only)'},
    },
  },
  seedance2_config: {
    type: 'object',
    required: false,
    description: 'Configuration for Seedance 2.0 AI video generation via fal.ai (only used when video_source is ai_seedance2)',
    properties: {
      function_mode: {'type': 'string', 'enum': ['text_to_video', 'image_to_video', 'reference_to_video'], 'default': 'text_to_video', 'description': 'Function mode: text_to_video, image_to_video (supports end_image_url for interpolation), or reference_to_video (up to 9 images / 3 videos / 3 audio refs)'},
      quality: {'type': 'string', 'enum': ['fast', 'standard'], 'default': 'fast', 'description': 'Quality tier: fast ($0.2419/s) or standard ($0.3034/s) at 720p'},
      duration: {'type': 'string', 'enum': ['4', '5', '8', '10', '15'], 'default': '5', 'description': 'Video duration in seconds'},
      resolution: {'type': 'string', 'enum': ['480p', '720p'], 'default': '720p', 'description': 'Output resolution'},
      aspect_ratio: {'type': 'string', 'enum': ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'], 'default': '16:9', 'description': 'Video aspect ratio'},
      generate_audio: {'type': 'boolean', 'default': true, 'description': 'Generate native audio with lip-sync (free, no extra cost)'},
    },
  },
  deforum_config: {
    type: 'object',
    required: false,
    description: 'Configuration for Deforum (local ComfyUI) schedule-driven animation (only used when video_source is ai_deforum)',
    properties: {
      base_model: {'type': 'string', 'enum': ['sd15', 'sdxl'], 'default': 'sd15', 'description': 'Base diffusion model'},
      num_frames: {'type': 'integer', 'default': 96, 'deprecated': true, 'description': 'auto-derived from duration; ignored if set. Set fps to control frame density.'},
      fps: {'type': 'integer', 'default': 12, 'description': 'Output frames per second (default 12, max 1000). User-selectable via UI.'},
      cadence: {'type': 'integer', 'default': 2, 'minimum': 1, 'maximum': 100, 'description': 'Diffusion cadence: 1 = every frame is a KSampler pass; N = 1 pass per N frames, intermediate frames warp+RAFT-flow interpolated. Max 100 (pinned node\'s own widget ceiling). Warp artifacts increase progressively: noticeable above 4, severe above 10.'},
      width: {'type': 'integer', 'default': 512, 'description': 'Frame width (SD1.5 ceiling 768, SDXL ceiling 1024)'},
      height: {'type': 'integer', 'default': 512, 'description': 'Frame height (SD1.5 ceiling 768, SDXL ceiling 1024)'},
      zoom_schedule: {'type': 'string', 'default': '0:(1.0008)', 'description': 'Zoom keyframe schedule, e.g. \'0:(1.0), 60:(1.5)\'. Default is a subtle continuous zoom-in, not flat 1.0 -- a frozen camera causes the img2img feedback loop to visibly degrade the image over time.'},
      translation_x_schedule: {'type': 'string', 'default': '0:(0)', 'description': 'Pan X keyframe schedule'},
      translation_y_schedule: {'type': 'string', 'default': '0:(0)', 'description': 'Pan Y keyframe schedule'},
      translation_z_schedule: {'type': 'string', 'default': '0:(0)', 'description': 'Dolly Z keyframe schedule'},
      rotation_3d_x_schedule: {'type': 'string', 'default': '0:(0)', 'description': 'Rotation X keyframe schedule (degrees)'},
      rotation_3d_y_schedule: {'type': 'string', 'default': '0:(0)', 'description': 'Rotation Y keyframe schedule (degrees)'},
      rotation_3d_z_schedule: {'type': 'string', 'default': '0:(0)', 'description': 'Rotation Z keyframe schedule (degrees)'},
      strength_schedule: {'type': 'string', 'default': '0:(0.65)', 'description': 'Denoising strength schedule (controls memory of previous frame)'},
      noise_schedule: {'type': 'string', 'default': '0:(0.02)', 'description': 'Noise schedule'},
      num_inference_steps: {'type': 'integer', 'default': 25, 'description': 'KSampler step count'},
      guidance_scale: {'type': 'number', 'default': 7.5, 'description': 'Classifier-free guidance scale'},
      prompt_keyframes: {'type': 'object', 'required': false, 'default': null, 'nullable': true, 'additionalProperties': {'type': 'string'}, 'description': 'LLM-driven prompt morphing keyframes. Keys are stringified frame indices (e.g. \'0\', \'75\', \'150\'); values are prompt texts. When set, overrides single-prompt mode and interpolates between prompts at the specified frames. Maximum key must be <= num_frames - 1.'},
    },
  },
  broll_upload_path: {
    type: 'string',
    required: false,
    nullable: true,
    description: 'Server-side path or URL to uploaded talking-head video. Required when video_source is upload_broll.',
  },
  broll_coverage: {
    type: 'string',
    required: false,
    default: 'balanced',
    enum: ['subtle', 'balanced', 'heavy', 'montage'],
    description: 'Target B-roll coverage budget: subtle (light cuts), balanced (moderate), heavy (aggressive), montage (maximum coverage)',
  },
  broll_visual_source: {
    type: 'string',
    required: false,
    default: 'mixed',
    enum: ['stock', 'ai_image', 'ai_video', 'mixed'],
    description: 'Source of B-roll visuals: stock footage, AI-generated images, AI-generated video clips, or a mix',
  },
  broll_ai_video_provider: {
    type: 'string',
    required: false,
    nullable: true,
    description: 'AI video provider for B-roll clips when broll_visual_source includes ai_video. Matches values in AI_VIDEO_SOURCE_METADATA (e.g. ai_kling, ai_veo, ai_animatediff).',
  },
  broll_min_segment_seconds: {
    type: 'number',
    required: false,
    default: 1.8,
    min: 0.8,
    max: 4.0,
    description: 'Minimum duration in seconds for a single B-roll segment',
  },
  broll_max_segment_seconds: {
    type: 'number',
    required: false,
    default: 8.0,
    min: 2.0,
    max: 15.0,
    description: 'Maximum duration in seconds for a single B-roll segment',
  },
  broll_min_speaker_gap_seconds: {
    type: 'number',
    required: false,
    default: 4.0,
    min: 1.0,
    max: 20.0,
    description: 'Minimum continuous speaker face-time in seconds that must remain between B-roll cuts',
  },
  broll_review_before_render: {
    type: 'boolean',
    required: false,
    default: false,
    description: 'When true, pause the pipeline after B-roll planning so the frontend can display and confirm the plan before final render',
  },
  broll_compose_mode: {
    type: 'string',
    required: false,
    default: 'fullcut',
    enum: ['fullcut', 'pip'],
    description: 'B-roll composition mode: fullcut replaces the full frame, pip overlays B-roll as picture-in-picture',
  },
  broll_audio_ducking: {
    type: 'boolean',
    required: false,
    default: false,
    description: 'When true, duck (lower) background audio volume during B-roll segments',
  },
  wan_local_config: {
    type: 'object',
    required: false,
    description: 'Configuration for local Wan video generation (only used when video_source is ai_wan21_t2v_1_3b, ai_wan22_ti2v_5b, ai_wan22_ti2v_5b_i2v, or ai_wan22_t2v_14b). Defaults for each field are applied from WAN_VARIANT_DEFAULTS on the backend.',
    properties: {
      variant: {'type': 'string', 'enum': ['wan21_1_3b', 'wan22_5b', 'wan22_14b'], 'description': 'Wan model variant discriminator. Must match the active video_source.'},
      steps: {'type': 'integer', 'description': 'Number of diffusion sampling steps. Range and default depend on the variant (1.3B: 20-50 default 30; 5B: 30-60 default 50; 14B+Lightning: 4-8 default 4).'},
      resolution: {'type': 'string', 'enum': ['832x480', '480x832', '1280x720', '720x1280'], 'description': 'Output resolution preset as WxH. Available values depend on the variant. Backend validates against WAN_VARIANT_DEFAULTS[variant].supported_resolutions.'},
      fps: {'type': 'integer', 'description': 'Output frames per second. Range and default depend on the variant.'},
      num_frames: {'type': 'integer', 'description': 'Total number of frames to generate. When omitted, derived from clip duration and fps. Capped at WAN_VARIANT_DEFAULTS[variant].max_frames.'},
      guidance_scale: {'type': 'number', 'description': 'Classifier-free guidance scale. 14B+Lightning works best at 1.0; 1.3B and 5B default to 7.5.'},
      seed: {'type': 'integer', 'default': null, 'nullable': true, 'description': 'Random seed for reproducibility. Null means random.'},
    },
  },
} as const;
